import { Workbox } from 'workbox-window';
import { create } from 'zustand';

/**
 * Обновяването никога не става мълчаливо. Шофьор, който тръгва към Мюнхен,
 * не бива да види друг интерфейс насред пътя — показваме лента „има нова
 * версия" и той решава кога да презареди (ADR 0009).
 */

interface UpdateState {
  updateReady: boolean;
  offlineReady: boolean;
  applyUpdate: () => void;
  setUpdateReady: (value: boolean) => void;
  setOfflineReady: (value: boolean) => void;
}

let applyPending: (() => void) | null = null;

export const useUpdateState = create<UpdateState>((set) => ({
  updateReady: false,
  offlineReady: false,
  applyUpdate: () => applyPending?.(),
  setUpdateReady: (updateReady) => set({ updateReady }),
  setOfflineReady: (offlineReady) => set({ offlineReady }),
}));

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  const workbox = new Workbox('/sw.js', { type: 'classic', scope: '/' });

  workbox.addEventListener('waiting', () => {
    applyPending = () => {
      workbox.addEventListener('controlling', () => window.location.reload());
      void workbox.messageSkipWaiting();
    };
    useUpdateState.getState().setUpdateReady(true);
  });

  workbox.addEventListener('installed', (event) => {
    if (!event.isUpdate) useUpdateState.getState().setOfflineReady(true);
  });

  void workbox.register();
}

const SYNC_TAG = 'kmdx-outbox';

/**
 * Фонова синхронизация там, където я има (Chrome/Android). Тя събужда
 * service worker-а, а той събужда отворените страници на приложението.
 * На iOS такова API няма — там опашката тръгва при отваряне на приложението
 * и при връщане на връзката. Затова и опашката е видима: шофьорът вижда
 * какво още не е тръгнало, вместо да предполага.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sync = (
      registration as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> };
      }
    ).sync;
    if (!sync) return false;
    await sync.register(SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

export function hasBackgroundSync(): boolean {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
}

/** Колко място заемат снимките в опашката — показва се в „Настройки". */
export async function storageEstimate(): Promise<{ usedBytes: number; quotaBytes: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usedBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? 0 };
}

/**
 * Моли браузъра да не изтрива базата при недостиг на място. Опашка със
 * заснето ЧМР не бива да изчезне, защото телефонът е пълен със снимки.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
